import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { Grid, Row, Col, Button } from '@sm/wds-react';
import { t, T } from '@sm/intl';

import useIsomorphicLayoutEffect from '../../../helpers/useIsomorphicLayoutEffect';
import getClickHandlers from '../../../helpers/getClickHandlers';
import { getLinkGenerator } from '../../../helpers/logging';
import addScrollListener from '../helpers/scrollListener';
import { MENU } from '../helpers/enums';
import logger from '../helpers/logger';
import COPY from '../helpers/copy';
import MENU_CONFIG from '../helpers/menuConfig';
import mrxLogoDark from '../helpers/logo-marketresearch.svg';
import mrxLogoLight from '../helpers/logo-marketresearch--white.svg';
import ExpandableMenu from '../ExpandableMenu';
import './nav-for-ie.scss';

export const TEST_ID = 'DesktopNavForIE';

const getLink = getLinkGenerator({
  ut_source: 'market_research',
  ut_source2: 'solutions',
  ut_source3: 'subnav',
});

const NavForIE = ({ openMenu, setOpenMenu }) => {
  const [scrolledToTop, setScrolledToTop] = useState(true);
  const navId = 'sm-mrx-nav-id';
  useIsomorphicLayoutEffect(() => {
    const removeListener = addScrollListener(isAtTop => setScrolledToTop(isAtTop));
    document.body.classList.add('sm-nav-for-ie--hide-overflow-x');
    return () => {
      removeListener();
      document.body.classList.remove('sm-nav-for-ie--hide-overflow-x');
    };
  });
  const classes = classnames({
    'sm-nav-for-ie__wrapper': true,
    'sm-nav-for-ie__wrapper--scroll': !scrolledToTop,
    'sm-nav-for-ie__wrapper--top': scrolledToTop,
  });
  const image = scrolledToTop ? mrxLogoLight : mrxLogoDark;
  const buttonColor = scrolledToTop ? 'muted' : 'upgrade';
  const buttonVariant = !scrolledToTop ? 'solid' : 'ghost';
  const buttonClass = classnames({
    'sm-nav-for-ie__cta-button': true,
    'sm-nav-for-ie__cta-button--light': scrolledToTop,
  });
  const textVariant = scrolledToTop ? 'light' : 'dark';

  return (
    <div className="sm-nav-for-ie__background-wrapper">
      <div className={classes} id={navId}>
        <Grid noBleeds noGutters fluid className="sm-nav-for-ie" data-testid={TEST_ID}>
          <Row>
            <Col className="sm-nav-for-ie__menu-col sm-nav-for-ie__logo-col">
              <a
                href={getLink('/market-research')}
                aria-label={t(COPY.homeLink)}
                {...getClickHandlers(() => {
                  logger.click.navigateTo('mrx_home');
                })}
              >
                <img src={image} alt={t(COPY.marketResearchSolutions)} className="sm-nav-for-ie__logo" />
              </a>
            </Col>
            <Col className="sm-nav-for-ie__menu-col wds-flex--x-center">
              <div className="sm-nav-for-ie__menu-item wds-p-r-7">
                <ExpandableMenu
                  {...MENU_CONFIG.solutions}
                  isOpen={openMenu === MENU.SOLUTIONS}
                  onClick={() => setOpenMenu(MENU.SOLUTIONS)}
                  onBlur={() => setOpenMenu(null)}
                  variant={textVariant}
                />
              </div>
              <div className="sm-nav-for-ie__menu-item wds-p-r-7">
                <ExpandableMenu {...MENU_CONFIG.services} variant={textVariant} onBlur={() => setOpenMenu(null)} />
              </div>
              <div className="sm-nav-for-ie__menu-item">
                <ExpandableMenu
                  {...MENU_CONFIG.moreResources}
                  isOpen={openMenu === MENU.MORE_RESOURCES}
                  onClick={() => setOpenMenu(MENU.MORE_RESOURCES)}
                  onBlur={() => setOpenMenu(null)}
                  variant={textVariant}
                />
              </div>
            </Col>
            <Col className="sm-nav-for-ie__menu-col sm-nav-for-ie__cta">
              <Button
                variant={buttonVariant}
                color={buttonColor}
                className={buttonClass}
                href={getLink('/market-research/get-a-demo', {
                  ut_ctatext: 'Get a demo',
                })}
                {...getClickHandlers(() => {
                  logger.click.navigateTo('get_a_demo');
                })}
              >
                <T desc={COPY.getADemo} />
              </Button>
            </Col>
          </Row>
        </Grid>
      </div>
    </div>
  );
};

NavForIE.propTypes = {
  openMenu: PropTypes.oneOf(Object.values(MENU)),
  setOpenMenu: PropTypes.func.isRequired,
};

NavForIE.defaultProps = {
  openMenu: undefined,
};

export default NavForIE;
